import type { FrameworkRef } from '@/sections/threats/types';
import type { RiskFrameworkEnrichment } from '@/sections/vulnerabilities/types/riskFrameworkEnrichment.types';

export const FRAMEWORK_KEY_TO_DISPLAY: Record<string, string> = {
  owasp_2021: 'OWASP Top 10 2021',
  owasp_llm: 'OWASP LLM Top 10',
  mitre_atlas: 'MITRE ATLAS',
  mitre_attack: 'MITRE ATT&CK',
  d3fend: 'MITRE D3FEND',
  nist_csf: 'NIST CSF',
  pci_dss: 'PCI DSS',
  hipaa: 'HIPAA',
  soc2: 'SOC 2',
  cis: 'CIS',
};

/**
 * Maps a raw backend framework key to its display label at render time.
 * Unknown keys fall back to the key itself.
 */
export function frameworkDisplayName(key: string): string {
  return FRAMEWORK_KEY_TO_DISPLAY[key] ?? key;
}

// Flat enrichment field → raw backend framework key. Only `atlas`/`attack`
// need translation; the remaining flat fields already use the raw key.
const ENRICHMENT_FLAT_FIELD_MAP: [keyof RiskFrameworkEnrichment, string][] = [
  ['owasp_2021', 'owasp_2021'],
  ['owasp_llm', 'owasp_llm'],
  ['atlas', 'mitre_atlas'],
  ['attack', 'mitre_attack'],
  ['d3fend', 'd3fend'],
  ['nist_csf', 'nist_csf'],
  ['pci_dss', 'pci_dss'],
  ['hipaa', 'hipaa'],
  ['soc2', 'soc2'],
  ['cis', 'cis'],
];

function frameworkRefsFromBackend(
  enrichment: RiskFrameworkEnrichment
): FrameworkRef[] {
  if (!enrichment.framework_refs?.length) return [];
  return enrichment.framework_refs.map(ref => ({
    framework: ref.framework,
    id: ref.id,
    name: ref.name ?? '',
  }));
}

function frameworkRefsFromFlatArrays(
  enrichment: RiskFrameworkEnrichment
): FrameworkRef[] {
  const refs: FrameworkRef[] = [];
  for (const [field, key] of ENRICHMENT_FLAT_FIELD_MAP) {
    const ids = enrichment[field] as string[] | undefined;
    if (!ids?.length) continue;
    for (const id of ids) {
      refs.push({ framework: key, id, name: '' });
    }
  }
  return refs;
}

/**
 * Converts risk framework enrichment into FrameworkRefs carrying RAW backend
 * framework keys (`mitre_attack`, `owasp_2021`, ...). Display labels are
 * applied only at render time via `frameworkDisplayName`, keeping the
 * vocabulary consistent with `threatIntel.framework_refs` from the CVE
 * enrichment pipeline.
 */
export function riskEnrichmentToFrameworkRefs(
  enrichment: RiskFrameworkEnrichment
): FrameworkRef[] {
  const fromBackend = frameworkRefsFromBackend(enrichment);
  if (fromBackend.length > 0) return fromBackend;
  return frameworkRefsFromFlatArrays(enrichment);
}

// Neutral, token-based badge style shared by every framework badge. Frameworks
// are distinguished by their label text, not color — per the Guard design
// system, which bans raw Tailwind palette colors and `dark:` variants in
// components and caps the palette at 3–5 colors.
export const DEFAULT_BADGE_COLOR =
  'bg-chariot-brand-primary/15 border-chariot-brand-primary/30 text-chariot-text-primary';

// Keyed by raw backend framework key (same vocabulary as FrameworkRef.framework).
// Every framework maps to the same neutral design-token style; the key set is
// retained so call sites can look up by framework without special-casing.
export const FRAMEWORK_BADGE_COLORS: Record<string, string> = {
  owasp_2021: DEFAULT_BADGE_COLOR,
  owasp_llm: DEFAULT_BADGE_COLOR,
  mitre_atlas: DEFAULT_BADGE_COLOR,
  mitre_attack: DEFAULT_BADGE_COLOR,
  d3fend: DEFAULT_BADGE_COLOR,
  nist_csf: DEFAULT_BADGE_COLOR,
  pci_dss: DEFAULT_BADGE_COLOR,
  hipaa: DEFAULT_BADGE_COLOR,
  soc2: DEFAULT_BADGE_COLOR,
  cis: DEFAULT_BADGE_COLOR,
};

export function sanitizeRiskNameForS3(name: string): string {
  return name.replaceAll('/', '_').replaceAll('\\', '_');
}

export function riskFrameworkS3Key(riskName: string): string {
  return `enrichments/risk-frameworks/${sanitizeRiskNameForS3(riskName)}.json`;
}

export function parseRiskFrameworkEnrichment(
  raw: unknown
): RiskFrameworkEnrichment | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as RiskFrameworkEnrichment;
    } catch {
      return null;
    }
  }
  return raw as RiskFrameworkEnrichment;
}
